import { formatDateAxisValue, niceAxisDomain } from './dataviz-utils';
import { fmtLocale, speakNumber } from './number-format';

import type { DataVizBlockData, ResolvedDataVizData } from '@agavi/blocks';

/**
 * What a chart IS, before any of it is played — its title, then each axis with its range and its
 * reference line.
 *
 * A sighted student reads both axes off the frame in a glance and then knows what the tones mean. A
 * listener was given the tones alone: a rising pitch says the value went up and nothing about from
 * what, to what, or in what.
 *
 * A FUNCTION rather than part of the player, because the sentences belong at the TOP of the block
 * and the player sits underneath the chart. Rendered from inside the player they arrived after
 * everything they were meant to introduce, and their read-through badges landed on the control row,
 * on top of the buttons' own numbers. The player still points at them with `aria-describedby`, so
 * a student who tabs straight to the control hears them on focus.
 *
 * Returned as separate CLAUSES, not one joined string. A full stop is the longest pause punctuation
 * can buy and it is already doing duty INSIDE a clause, between an axis's range and its reference
 * line — so the break before "x axis" was no longer than the one before "Reference line", and the
 * two axes ran together. One paragraph each gives a real block boundary: a reader pauses between
 * blocks, the read-through gets a row per clause, and read-aloud speaks them as separate utterances.
 */
/**
 * The chart type as a word, an exhaustive switch of LITERAL `t()` calls.
 *
 * Not `t(`blocks.dataviz.chartType.${type}`)`: a template key trips the dynamic-key ceiling in
 * `invariants.test.ts` and is invisible to the every-key-exists check. This way a fourth chart type
 * fails to compile until it has a name in both locales.
 */
function chartTypeName(type: DataVizBlockData['chartType'], t: (key: string) => string): string {
  switch (type) {
    case 'line': return t('blocks.dataviz.chartType.line');
    case 'bar': return t('blocks.dataviz.chartType.bar');
    case 'scatter': return t('blocks.dataviz.chartType.scatter');
  }
}

export function chartOrientation(
  data: ResolvedDataVizData,
  locale: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string[] {
  const playable = data.series.filter(s => s.points.length > 0);
  const selected = playable.find(s => s.id === data.sonificationSeriesId) ?? playable[0];
  const pts = selected?.points ?? [];
  if (!pts.length) return [];

  const parts: string[] = [];
  const say = (v: number) => speakNumber(fmtLocale(v, locale), t);
  const sayX = (v: number) => (data.xAxisType === 'date' ? formatDateAxisValue(v) : say(v));
  /**
   * Named, and typed.
   *
   * A title on its own in the read-through is just a sentence — it could be a heading, or authored
   * prose above the block. Saying what it IS matches the `y axis:` / `x axis:` clauses beside it,
   * and "graph" rather than "chart" because that is the word the play button now uses too.
   *
   * The TYPE matters more here than it looks: line, bar and scatter answer different questions, and
   * it is the one thing about the picture that changes how the tones should be read — a scatter has
   * no line between its points, so a run of pitches is a cloud rather than a trend. A sighted
   * student has it at a glance.
   *
   * Untitled charts still say their type, since that is the half that is never missing.
   */
  const type = chartTypeName(data.chartType, t);
  parts.push(data.title?.trim()
    ? t('blocks.dataviz.sonification.graphTitle', { title: data.title.trim(), type })
    : t('blocks.dataviz.sonification.graphTitleUntitled', { type }));

  /**
   * The DRAWN y range, from the same `niceAxisDomain` the chart itself is given.
   *
   * It used to be the selected series' own maximum, so an axis topping out at 1.8 was announced as
   * 1.62. There is no way to ask Recharts what bound it picked, so the domain is ours and both
   * callers read it from here. Across EVERY series, because that is what the axis spans.
   */
  const [yLo, yHi] = niceAxisDomain(
    data.series.flatMap(s => s.points.map(p => p.y)),
    data.sonificationThresholds?.y,
    data.chartType === 'bar' ? 0 : undefined,
  );
  if (data.yAxisLabel?.trim()) {
    let line = t('blocks.dataviz.sonification.axisRange', {
      axis: t('blocks.dataviz.sonification.axisY'),
      label: data.yAxisLabel.trim(),
      min: say(yLo),
      max: say(yHi),
    });
    if (data.sonificationThresholds?.y != null) {
      line += `. ${t('blocks.dataviz.sonification.referenceAt', { value: say(data.sonificationThresholds.y) })}`;
    }
    parts.push(line);
  }

  // The x range stays the data's own bounds, because that is literally what `<XAxis>` is set to
  // (`dataMin`/`dataMax`). A categorical axis has no range to state, so it says how many categories
  // there are instead.
  const xs = pts.map(p => p.x).filter((x): x is number => typeof x === 'number');
  if (data.xAxisLabel?.trim()) {
    let line = xs.length === pts.length
      ? t('blocks.dataviz.sonification.axisRange', {
        axis: t('blocks.dataviz.sonification.axisX'),
        label: data.xAxisLabel.trim(),
        min: sayX(Math.min(...xs)),
        max: sayX(Math.max(...xs)),
      })
      : t('blocks.dataviz.sonification.axisCategories', {
        axis: t('blocks.dataviz.sonification.axisX'),
        label: data.xAxisLabel.trim(),
        count: pts.length,
      });
    if (data.sonificationThresholds?.x != null) {
      line += `. ${t('blocks.dataviz.sonification.referenceAt', { value: sayX(data.sonificationThresholds.x) })}`;
    }
    parts.push(line);
  }
  return parts;
}

/** One id per clause, stable for a given block, so `aria-describedby` can name them all. */
export function orientationIdsFor(baseId: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => `${baseId}-orient-${i}`);
}
